import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type CurrentUser, type SheetRunWithDetail, type SheetSyncResult } from '../lib/api';
import { SheetPreview } from './SheetPreview';
import { SheetCoverage } from './SheetCoverage';
import { SheetRunHistory } from './SheetRunHistory';

/**
 * The Google Sheet, as a place rather than a settings screen.
 *
 * A pull used to report itself as six counters and a bulleted list of problem
 * rows, which left the two questions people actually have unanswerable: what
 * will change, and is the sheet wired to the products I think it is. Both are
 * answered by the same payload the run already computes, so this tab is mostly
 * a matter of showing it.
 *
 * Opening this tab reads the database, never the sheet: the last recorded run
 * is the answer until someone asks for a fresher one. That keeps a tab switch
 * free, and means a preview stays on screen to come back to.
 *
 * Configuration stays in Admin → Google Sheet sync. This is for running a pull
 * and reading the result.
 */

type Section = 'preview' | 'coverage' | 'history';

export function SheetSyncTab({ user }: { user: CurrentUser }) {
  const queryClient = useQueryClient();
  const canRun = user.role === 'admin' || user.role === 'editor';

  const [section, setSection] = useState<Section>('preview');
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [skuFilterText, setSkuFilterText] = useState('');
  const [fresh, setFresh] = useState<SheetSyncResult | null>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'warn' | 'error'; text: string } | null>(null);

  const requestedSkus = () =>
    skuFilterText
      .split(/[,\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  /**
   * What the buttons are about to run against. Derived once so the labels, the
   * confirm dialog and the request itself can never disagree — and matched
   * loosely on purpose: the server normalizes these the same way it normalizes
   * a sheet cell, so `nwc-chair` finds the row typed `NWC-CHAIR`.
   */
  const narrowed = requestedSkus();

  /** How a run started now would be recorded — see SheetSyncRun.sweep. */
  const currentSweep = narrowed.length > 0 ? 'partial' : 'full';

  const latest = useQuery({
    queryKey: ['sheet-detail'],
    queryFn: api.sheetDetailLatest,
    throwOnError: false,
  });

  // Only when a specific past run was opened from the history list.
  const opened = useQuery({
    queryKey: ['sheet-run-detail', openRunId],
    queryFn: () => api.sheetRunDetail(openRunId!),
    enabled: openRunId !== null,
    throwOnError: false,
  });

  const run = useMutation({
    mutationFn: (dryRun: boolean) => api.runSheetSync(dryRun, narrowed),
    onSuccess: (result) => {
      setFresh(result);
      setOpenRunId(null);
      setSection('preview');
      void queryClient.invalidateQueries({ queryKey: ['sheet-detail'] });
      void queryClient.invalidateQueries({ queryKey: ['sheet-history'] });
      if (!result.dryRun) {
        // A real pull stages into the same queue an inline edit lands in.
        void queryClient.invalidateQueries({ queryKey: ['queue'] });
        void queryClient.invalidateQueries({ queryKey: ['products'] });
      }
      if (result.error) setToast({ kind: 'error', text: result.error });
      else if (result.refusedReason) setToast({ kind: 'warn', text: result.refusedReason });
      else if (result.dryRun) setToast({ kind: 'ok', text: 'Preview only — nothing was staged.' });
      else
        setToast({
          kind: 'ok',
          text: `${result.staged} change${result.staged === 1 ? '' : 's'} staged into Pending. Nothing is live until it is pushed from there.`,
        });
    },
    onError: (error: ApiError) =>
      setToast({
        kind: 'error',
        text:
          error.status === 409
            ? 'A sync is already running — the sheet takes the same lock. Try again in a moment.'
            : error.message,
      }),
  });

  const startPull = () => {
    const scope =
      narrowed.length > 0
        ? `only ${narrowed.length === 1 ? narrowed[0] : `these ${narrowed.length} SKUs`}`
        : 'the whole sheet';

    // Only quote a number the preview on screen actually stands behind: a
    // whole-sheet preview says nothing about a run narrowed to one SKU, and
    // vice versa.
    const comparable = shown !== null && shown.detail.error === null && shown.sweep === currentSweep;
    const staged = comparable
      ? shown.detail.rows.reduce((total, row) => total + row.cells.filter((c) => c.outcome === 'stage').length, 0)
      : null;
    const expected = staged === null ? '' : `The last preview expected ${staged} change${staged === 1 ? '' : 's'}. `;

    if (
      !window.confirm(
        `Pull ${scope} now?\n\n${expected}Changes are staged into Pending, not pushed — nothing reaches a channel until someone syncs from there.`,
      )
    ) {
      return;
    }
    run.mutate(false);
  };

  // What the preview pane is showing, in priority order: a run just triggered,
  // a run opened from history, or the last one on record.
  const shown: SheetRunWithDetail | null = fresh?.detail
    ? ({
        ...emptyRunMeta,
        id: fresh.runId ?? '',
        startedAt: fresh.detail.builtAt,
        finishedAt: fresh.detail.builtAt,
        rowsRead: fresh.rowsRead,
        staged: fresh.staged,
        unchanged: fresh.unchanged,
        unmatched: fresh.unmatched,
        rejected: fresh.rejected,
        collisions: fresh.collisions,
        ok: fresh.ok,
        dryRun: fresh.dryRun,
        sweep: currentSweep,
        triggeredBy: user.email,
        detail: fresh.detail,
      } satisfies SheetRunWithDetail)
    : openRunId
      ? opened.data?.run ?? null
      : latest.data?.run ?? null;

  const busy = run.isPending;

  return (
    <>
      <div className="card sheet-summary">
        <div>
          <h3>Google Sheet</h3>
          <p className="small muted">
            A pull plans every row and stages what changed into <strong>Pending</strong>. Nothing
            reaches a channel from here.
          </p>
        </div>

        <div className="spacer" />

        {canRun && (
          <div className="sheet-run-controls">
            {/* Labelled rather than placeholder-only: this is the control that
                makes trying a change safe, so it has to be findable before
                someone reaches for the button next to it. */}
            <div className="field">
              <label htmlFor="sheetSkuFilter">Only these SKUs</label>
              <input
                id="sheetSkuFilter"
                type="text"
                value={skuFilterText}
                placeholder="blank = the whole sheet"
                onChange={(e) => setSkuFilterText(e.target.value)}
                disabled={busy}
              />
              <span className="small muted">
                {narrowed.length > 0 ? (
                  <>
                    Both buttons will touch{' '}
                    <strong>
                      {narrowed.length} row{narrowed.length === 1 ? '' : 's'}
                    </strong>{' '}
                    only — every other row is left completely untouched.
                  </>
                ) : (
                  'Comma or newline separated, up to 50. The safe way to try one product first: every other row gets no change, no note and no write-back.'
                )}
              </span>
            </div>

            <div className="sheet-run-buttons">
              <button disabled={busy} onClick={() => run.mutate(true)}>
                {run.isPending && run.variables === true
                  ? 'Reading the sheet…'
                  : narrowed.length > 0
                    ? `Preview ${narrowed.length}`
                    : 'Preview pull'}
              </button>
              <button className="primary" disabled={busy} onClick={startPull}>
                {run.isPending && run.variables === false
                  ? 'Pulling…'
                  : narrowed.length > 0
                    ? `Pull ${narrowed.length}`
                    : 'Pull now'}
              </button>
            </div>
          </div>
        )}
      </div>

      {toast && (
        <div className={`alert ${toast.kind}`} onClick={() => setToast(null)} role="status">
          {toast.text}
        </div>
      )}

      <div className="card section-switch">
        <button className={section === 'preview' ? 'active' : ''} onClick={() => setSection('preview')}>
          What a pull does
        </button>
        <button className={section === 'coverage' ? 'active' : ''} onClick={() => setSection('coverage')}>
          Missing from the sheet
        </button>
        <button className={section === 'history' ? 'active' : ''} onClick={() => setSection('history')}>
          Runs
        </button>
      </div>

      {section === 'preview' && (
        <PreviewSection
          shown={shown}
          loading={latest.isLoading || opened.isFetching}
          error={(latest.error ?? opened.error) as Error | null}
          canRun={canRun}
          onBackToLatest={openRunId ? () => setOpenRunId(null) : null}
        />
      )}

      {section === 'coverage' && <SheetCoverage />}

      {section === 'history' && (
        <SheetRunHistory
          openRunId={openRunId}
          onOpen={(id) => {
            setOpenRunId(id);
            setFresh(null);
            setSection('preview');
          }}
        />
      )}
    </>
  );
}

function PreviewSection({
  shown,
  loading,
  error,
  canRun,
  onBackToLatest,
}: {
  shown: SheetRunWithDetail | null;
  loading: boolean;
  error: Error | null;
  canRun: boolean;
  onBackToLatest: (() => void) | null;
}) {
  if (loading) return <div className="empty-state">Loading the last run…</div>;
  if (error) return <div className="alert error">{error.message}</div>;

  if (!shown) {
    return (
      <div className="empty-state">
        <p>No pull on record yet.</p>
        <p className="small">
          {canRun
            ? 'Run a preview: it reads the sheet and plans every row without staging or writing anything.'
            : 'Once someone runs a pull, what it did shows up here.'}
        </p>
      </div>
    );
  }

  const { detail } = shown;

  if (detail.version !== 1) {
    return (
      <div className="alert warn">
        This run was recorded by an older version of the app and its detail cannot be read. Run a
        fresh preview.
      </div>
    );
  }

  return (
    <>
      <div className="toolbar sheet-run-meta">
        <span className={`badge ${shown.dryRun ? '' : 'ok'}`}>{shown.dryRun ? 'preview' : 'applied'}</span>
        <span className="small muted">
          {shown.dryRun ? 'Planned' : 'Pulled'} {new Date(shown.startedAt).toLocaleString()}
          {shown.triggeredBy ? ` by ${shown.triggeredBy}` : ' on schedule'} · {detail.tabName}
        </span>
        {shown.rowsRead > detail.rows.length && (
          <span className="badge warn" title="A SKU filter restricted this run to a few rows">
            {detail.rows.length} of {shown.rowsRead} rows
          </span>
        )}
        {onBackToLatest && (
          <button className="link small" onClick={onBackToLatest}>
            Back to the latest run
          </button>
        )}
      </div>

      {detail.error && (
        <div className="alert error">
          This run never got as far as planning: {detail.error}
        </div>
      )}

      {detail.refusedReason && (
        <div className="alert warn">
          <strong>The whole run was refused, and nothing was staged.</strong> {detail.refusedReason}.
          The prices below are what it would have staged — check the sheet before raising the limit in
          Admin.
        </div>
      )}

      {detail.skuAllowlistMisses.length > 0 && (
        <div className="alert warn small">
          Asked for but not found in the sheet: {detail.skuAllowlistMisses.join(', ')} — likely a typo,
          since a SKU with no row can never be pulled.
        </div>
      )}

      {detail.error === null && <SheetPreview detail={detail} />}
    </>
  );
}

/** The fields a just-finished run does not report back, filled in for display. */
const emptyRunMeta = {
  id: '',
  startedAt: '',
  finishedAt: null as string | null,
  rowsRead: 0,
  staged: 0,
  unchanged: 0,
  unmatched: 0,
  rejected: 0,
  collisions: 0,
  ok: false,
  dryRun: true,
  sweep: null as string | null,
  triggeredBy: null as string | null,
  hasDetail: true,
};
